import { useState } from 'react'
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { tokenManager } from '@/lib/tokenManager'
import type { ChatMessagePart } from '../types'
import { ShareButton } from './actionBar'

type ArtifactPart = Extract<ChatMessagePart, { kind: 'artifact' }>

function humanSize(bytes: number | undefined): string {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

interface Props {
  part: ArtifactPart
  onShare?: () => void
}

export function ArtifactCard({ part, onShare }: Props) {
  const isPdf = part.mime === 'application/pdf' || part.filename.toLowerCase().endsWith('.pdf')
  const Icon = isPdf ? FileText : FileSpreadsheet
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The artifact download route is gated on ai.chatbot and reads the
  // Bearer token from the Authorization header. A plain <a href=…>
  // click doesn't attach that header, so the request 401s and the
  // browser reports "File wasn't available on site". Instead fetch
  // the bytes as a blob, turn the blob into an object URL, and trigger
  // a programmatic anchor click to write the file to disk.
  const download = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const token = tokenManager.get()
      const res = await fetch(`/api/chatbot/artifacts/${part.id}`, {
        credentials: 'same-origin',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = part.filename || (isPdf ? 'report.pdf' : 'report.csv')
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="my-3 flex items-center gap-3 rounded-lg border border-border bg-card p-3">
      <div className="flex size-10 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
        <Icon className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-foreground">{part.filename}</div>
        <div className="text-[11px] text-muted-foreground">
          {isPdf ? 'PDF' : 'CSV'} · {humanSize(part.size)}
          {error ? <span className="ml-2 text-destructive">{error}</span> : null}
        </div>
      </div>
      <Button size="sm" variant="outline" onClick={download} disabled={busy}>
        {busy ? (
          <Loader2 className="mr-1.5 size-3.5 animate-spin" />
        ) : (
          <Download className="mr-1.5 size-3.5" />
        )}
        {busy ? 'Preparing…' : 'Download'}
      </Button>
      <ShareButton onClick={onShare} />
    </div>
  )
}
